import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { Company } from '../../../../domains/companies/models';
import { SmsService } from '../../../../domains/sms/services/sms.service';
import { Subscription } from 'rxjs';
import { DidByCompanyIdResponse, SmsSenderResponse } from 'voxloud-types/dist/models';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { finalize } from 'rxjs/operators';
import { NotificationService } from '../../../../shared/services/notification/notification.service';
import { AddEditSenderPopupComponent } from '../add-edit-sender-popup/add-edit-sender-popup.component';
import { TranslateService } from '@ngx-translate/core';
import { DidsService } from '../../../../domains/dids/services';
import { DID } from 'voxloud-types/dist/models';
import { LinksPopupComponent } from '../../../../shared/components/links-popup/links-popup.component';
@Component({
  selector: 'gdp-company-sms',
  templateUrl: './company-sms.component.html',
  styleUrls: ['./company-sms.component.scss']
})
export class CompanySmsComponent implements OnInit, OnDestroy {
  @Input() company: Company | null;
  public senders: SmsSenderResponse[] = [];
  private subscriptions: Subscription[] = [];
  public loading: boolean = false;
  private companyDids: DID[] = [];

  constructor(public smsService: SmsService,
    private modalService: NgbModal,
    private translate: TranslateService,
    private notification: NotificationService,
    private didsService: DidsService) { }
  
  ngOnInit() {
    this.getSenderDetails();
    this.getDids();
  }

  public getSenderDetails(): void {
    if (!this.company) {
      return;
    }
    this.loading = true;
    this.subscriptions.push(this.smsService.getSendersDetails(this.company.companyId).subscribe((senders) => {
      this.senders = senders;
      this.loading = false;
    }, (err) => {
      this.senders = [];
      this.loading = false;
    }));
  }

  private openSenderModal(isEdit: boolean, sender?: SmsSenderResponse): void {
    const modal = this.modalService.open(AddEditSenderPopupComponent, { size: 'md' });
    Object.assign(modal.componentInstance, {
      title: isEdit ? 'company.sms.edit.title' : 'company.sms.add.title',
      senderDetails: {
        label: isEdit ? 'company.sms.edit.form.name.label' : 'company.sms.add.form.name.label',
        placeholder: isEdit ? 'company.sms.edit.form.name.placeholder' : 'company.sms.add.form.name.placeholder',
        senderResponse: sender || '',
        senderType: isEdit ? 'company.sms.edit.form.sender-type' : 'company.sms.add.form.sender-type',
        provider: isEdit ? 'company.sms.edit.form.select-did' : 'company.sms.add.form.select-did',
        companyDids: this.companyDids
      },
      btnText: isEdit ? 'company.sms.edit.form.button' : 'company.sms.add.form.button',
      isEditMode: isEdit,
      companyId: this.company.companyId,
      senderId: sender?.id
    });
    this.subscriptions.push(modal.componentInstance.onEmitAddSender.subscribe(() => {
      this.getSenderDetails();
    }));
    this.subscriptions.push(modal.componentInstance.onEmitCompanyDids.subscribe((dids) => {
      this.companyDids = dids;
    }));
  }

  public addNewSender(): void {
    this.openSenderModal(false);
  }

  public editSender(sender: SmsSenderResponse): void {
    this.openSenderModal(true, sender);
  }

  public deleteSender(sender: SmsSenderResponse): void {
    this.notification.showConfirmation(
      this.translate.instant(
        'company.sms.delete.text',
        { senderName: sender.name }
      )
    ).then((result: string) => {
      if (result === 'ok') {
        this.loading = true;
        const sub = this.smsService.deleteSendersDetails(this.company.companyId, sender.id)
          .pipe(finalize(() => this.loading = false))
          .subscribe({
            next: () => {
              this.getSenderDetails();
              this.notification.success('company.sms.success.delete');
            },
            error: (err: HttpErrorResponse) => {
              const links = err.error?.errors?.[0]?.rejectedValue;
              if (err.status === 409 && links != null) {
                const modal = this.modalService.open(LinksPopupComponent, { size: 'lg' });
                modal.componentInstance.links = links;
                modal.componentInstance.title = 'company.sms.links-popup.title';
                modal.componentInstance.message = 'company.sms.links-popup.message';
              }
            }
          });
        this.subscriptions.push(sub);
      } else {
        this.loading = false;
      }
    })
  }

  public getDids(): void {
    this.subscriptions.push(this.didsService.getDidsByCompanyId(this.company.companyId)
      .subscribe(
        (response: DidByCompanyIdResponse[]) => {
          if (response && response.length > 0) {
            this.companyDids = response[0].dids.filter(did => !did.is_from_pool);
          }
        }
      ));
  }

  ngOnDestroy() {
    this.subscriptions.forEach(s => s?.unsubscribe());
  }
}
